import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import GUI from "lil-gui";

/**
 * Base
 */
// Debug
const gui = new GUI();
const parameters = {
  planetColor: "#a9b7fe",
  planetRadius: 1,
  count: 10000,
  size: 0.01,
  radius: 5,
  rings: 3,
  randomness: 0.2,
  insideColor: "#2270ec",
  outsideColor: "#c800ff",
};
// Canvas
const canvas = document.querySelector("canvas.webgl");
const abmientLight = new THREE.AmbientLight();

// Scene
const scene = new THREE.Scene();
scene.add(abmientLight);
const textureLoader = new THREE.TextureLoader();

const arm = textureLoader.load("./textures/rock_01_arm_1k.jpg");
const diff = textureLoader.load("./textures/8k_jupiter.webp");
const normal = textureLoader.load("./textures/rock_01_nor_gl_1k.png");

// const axesHelper = new THREE.AxesHelper(2);
// scene.add(axesHelper);

// Planet and Rings
let planetGeo = null;
let planetMat = null;
let planet = null;

let geometry = null;
let material = null;
let points = null;
let group = null;

const generatePlanet = () => {
  if (points !== null) {
    geometry.dispose();
    material.dispose();
    planetGeo.dispose();
    planetMat.dispose();

    group.remove(points);
    group.remove(planet);
    scene.remove(group);
  }

  // Create Group
  group = new THREE.Group();

  // Planet
  planetGeo = new THREE.IcosahedronGeometry(parameters.planetRadius, 20);
  planetMat = new THREE.MeshStandardMaterial({
    color: parameters.planetColor,
    map: diff,
    aoMap: arm,
    blending:THREE.AdditiveBlending,
    roughnessMap: arm,
    metalnessMap: arm,
    normalMap: normal,
  });
  planet = new THREE.Mesh(planetGeo, planetMat);
  group.add(planet);

  // Rings
  geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(parameters.count * 3);
  const colors = new Float32Array(parameters.count * 3);

  // Color Class
  const innerColor = new THREE.Color(parameters.insideColor);
  const outerColor = new THREE.Color(parameters.outsideColor);

  const innerRadius = parameters.planetRadius;
  const outerRadius = Math.max(
    parameters.radius,
    parameters.planetRadius + 0.3
  );
  const ringSystemWidth = outerRadius - innerRadius;
  const ringWidth = ringSystemWidth / parameters.rings;

  for (let i = 0; i < parameters.count; i++) {
    const i3 = i * 3;

    const ringIndex = i % parameters.rings;

    // Add gap to distinguish rings
    const gap = ringWidth * 0.3;

    // Calculate boundaries for the current ring
    const currentRingInner = innerRadius + ringIndex * ringWidth + gap;
    const currentRingOuter = innerRadius + (ringIndex + 1) * ringWidth - gap;

    const radius =
      Math.random() * (currentRingOuter - currentRingInner) + currentRingInner;
    const angle = Math.random() * Math.PI * 2;

    const randomY = (Math.random() - 0.5) * parameters.randomness;

    positions[i3] = Math.cos(angle) * radius;
    positions[i3 + 1] = randomY;
    positions[i3 + 2] = Math.sin(angle) * radius;

    // Colorsconst
    const mixedColor = innerColor.clone();
    mixedColor.lerp(outerColor, radius / parameters.radius);
    colors[i3] = mixedColor.r;
    colors[i3 + 1] = mixedColor.g;
    colors[i3 + 2] = mixedColor.b;
  }

  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  // Material
  material = new THREE.PointsMaterial({
    size: parameters.size,
    sizeAttenuation: true,
    blending: THREE.AdditiveBlending,
    vertexColors: true,
  });

  points = new THREE.Points(geometry, material);
  group.add(points);
  scene.add(group);
};

generatePlanet();

// GUI
gui
  .addColor(parameters, "planetColor")
  .name("Planet Color")
  .onFinishChange(generatePlanet);
gui
  .add(parameters, "planetRadius")
  .min(0.5)
  .max(2)
  .step(0.001)
  .name("Planet Radius")
  .onFinishChange(generatePlanet);
// Rings
gui
  .add(parameters, "rings")
  .min(1)
  .max(20)
  .step(1)
  .name("Rings")
  .onFinishChange(generatePlanet);
gui
  .add(parameters, "radius")
  .min(0.7)
  .max(10)
  .step(0.01)
  .name("Ring Radius")
  .onFinishChange(generatePlanet);

gui
  .add(parameters, "randomness")
  .min(0)
  .max(2)
  .step(0.001)
  .name("Ring Height Displacement")
  .onFinishChange(generatePlanet);
gui
  .addColor(parameters, "insideColor")
  .name("Inner Ring Color")
  .onFinishChange(generatePlanet);
gui
  .addColor(parameters, "outsideColor")
  .name("Outer Ring Color")
  .onFinishChange(generatePlanet);
// Particles
gui
  .add(parameters, "size")
  .min(0.001)
  .max(0.1)
  .step(0.001)
  .name("Particle Size")
  .onFinishChange(generatePlanet);
gui
  .add(parameters, "count")
  .min(100)
  .max(1000000)
  .step(100)
  .name("Particle Count")
  .onFinishChange(generatePlanet);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

window.addEventListener("resize", () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
  75,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = 1;
camera.position.y = 1;
camera.position.z = 3;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
  const elapsedTime = clock.getElapsedTime();
  group.rotation.y = elapsedTime * 0.05;
  group.rotation.x = Math.PI / 13;
  // Update controls
  controls.update();

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
